import { Box, Flex, Text } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { assessmentBadges } from 'src/components/Review/Badges';
import { FileListDisplay } from '../GenericFieldComponents/FileList/FileListDisplay';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import {
  IAdjudicationListItem,
  IEarlyAssessmentListItem
} from '@publishing-engine/domain/views';
import { formatDate } from 'src/utils/date';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { allKeysHaveValues, useOptInsData } from 'src/pages/MyTask/Assessments/hooks';
import ReviewTableScore from 'src/components/ReviewTableScore';
import AssessmentOptIns from 'src/components/Assessment/AssessmentOptIns';

interface Props {
  onClose: () => void;
  assessmentData: IEarlyAssessmentListItem | IAdjudicationListItem | null;
  type: 'Adjudication' | 'Early Assessment';
}

const ViewAssessmentModal = ({ onClose, assessmentData, type }: Props) => {
  const earlyAssessment =
    type === 'Adjudication'
      ? (assessmentData as IAdjudicationListItem)?.caseData?.adjudication
      : (assessmentData as IEarlyAssessmentListItem)?.caseData?.assessment;

  const submissionScore = earlyAssessment && earlyAssessment?.submissionScores;
  const additionalReference = earlyAssessment && 'missingReference' in earlyAssessment ? earlyAssessment?.missingReference : undefined

  const { workflowResponse: workflowData } = useSubmissionData()

  const { config } = useOptInsData(workflowData, assessmentData?.submissionContextId || "")
  const hasOptIns = earlyAssessment?.webOfScienceOptIn || earlyAssessment?.reviewerAnonymity || earlyAssessment?.peerReviewTransparency
  const isSubmissionScoresData = allKeysHaveValues(submissionScore)


  return (
    <Overlay height="100%">
      <Flex
        width="100vw"
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
      >
        <Layout width="48rem">
          <Flex flexDirection="column" gap={6} padding={8}>
            <ButtonText
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              maxWidth="fit-content"
              onClick={onClose}
              variant="secondary"
            >
              Back
            </ButtonText>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingSection">{type}</Text>
              <Text textStyle="textBody">
                By {assessmentData?.expert?.firstName}{' '}
                {assessmentData?.expert?.lastName}, on{' '}
                {formatDate(
                  new Date(
                    assessmentData?.workflow?.dateEnteredCurrentPhase ?? ''
                  )
                )}
              </Text>
            </Flex>

            <Flex
              flexDirection="column"
              gap={6}
              backgroundColor="surfacePrimaryFill"
              padding={8}
            >
              {earlyAssessment?.recommendation && (
                <Flex flexDirection="column" gap={6}>
                  <Text textStyle="headingGroup">Recommendation</Text>
                  <Flex>
                    {assessmentBadges[earlyAssessment.recommendation]}
                  </Flex>
                </Flex>
              )}
              {isSubmissionScoresData && submissionScore && config && (
                <Box flexDirection="column">
                  <Text textStyle="headingGroup">Submission Scores</Text>
                  <Box marginX={2} marginTop={4}>
                    <ReviewTableScore
                      submissionScores={submissionScore}
                      reviewFieldConfig={config}
                    />
                  </Box>
                </Box>
              )}
              {(earlyAssessment?.feedbackForEditors || (earlyAssessment?.editorAttachments &&
                earlyAssessment?.editorAttachments?.length > 0) || earlyAssessment?.recommendedReviewers?.exists) &&
                <>
                  <Text textStyle="headingGroup">Communications For Editors</Text>
                  {earlyAssessment?.feedbackForEditors &&
                    <><Text textStyle="headingItem">Feedback for Editors</Text>
                      <Text textStyle="textBody">
                        {earlyAssessment?.feedbackForEditors}
                      </Text>
                    </>}
                  {earlyAssessment?.editorAttachments &&
                    earlyAssessment?.editorAttachments?.length > 0 && (
                      <>
                        <Text textStyle="headingItem">Attached Files</Text>
                        <Flex flexDirection="column" gap={4}>
                          <FileListDisplay
                            files={earlyAssessment?.editorAttachments}
                            buttonIcon={true}
                          />
                        </Flex>
                      </>
                    )}
                  {earlyAssessment?.recommendedReviewers?.exists && (
                    <>
                      <Text textStyle="headingItem">Recommended Reviewers</Text>
                      {earlyAssessment?.recommendedReviewers?.value.map(item => (
                        <Flex flexDirection="column" gap={2}>
                          <Text textStyle="headingItem">
                            {item.firstName} {item.lastName}
                          </Text>
                          <Flex gap={1} alignItems="center">
                            <Text
                              noOfLines={1}
                              overflow="hidden"
                              textOverflow="ellipsis"
                            >
                              {item.affiliation}
                            </Text>
                            {item.ringgoldId && <RinggoldBadge />}
                          </Flex>
                          <Text textStyle="textBody">{item.email}</Text>
                          <Text textStyle="textBody">{item.reason}</Text>
                        </Flex>
                      ))}
                    </>
                  )}
                </>}

              {(earlyAssessment?.feedbackForAuthors ||
                (earlyAssessment?.authorsAttachments &&
                  earlyAssessment?.authorsAttachments?.length > 0)) && (
                  <Text textStyle="headingGroup">Communications For Authors</Text>
                )}

              {earlyAssessment?.feedbackForAuthors && (
                <>
                  <Text textStyle="headingItem">Feedback for Authors</Text>
                  <Text textStyle="textBody">
                    {earlyAssessment?.feedbackForAuthors}
                  </Text>
                </>
              )}
              {earlyAssessment?.authorsAttachments &&
                earlyAssessment?.authorsAttachments?.length > 0 && (
                  <Text textStyle="headingItem">Attached Files</Text>
                )}
              <Flex flexDirection="column" gap={4}>
                {earlyAssessment?.authorsAttachments &&
                  earlyAssessment?.authorsAttachments?.length > 0 && (
                    <FileListDisplay
                      files={earlyAssessment?.authorsAttachments}
                      buttonIcon={true}
                    />
                  )}
              </Flex>
              {type === 'Adjudication' && additionalReference?.exists && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">
                    Additional References
                  </Text>
                  <Text textStyle="textBody">{additionalReference?.value}</Text>
                </Flex>
              )}
              {earlyAssessment && hasOptIns && <>
                <Text textStyle="headingGroup">Opt Ins</Text>
                <AssessmentOptIns
                  earlyAssessment={earlyAssessment}
                  config={config}
                />
              </>}
            </Flex>
          </Flex>
        </Layout>
      </Flex>
    </Overlay>
  );
};

export default ViewAssessmentModal;
